import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  ChooseStepModal,
  CreateAutomatedEmail,
  CreateCall,
  CreateTodo,
} from "./components/Steps";
import { useNavigate, useParams } from "react-router-dom";
import { sequenceEndPoints } from "../../utils/constants/httpConstants";
import {
  EMITTER_SUCCESS,
  STEP_TYPE_COMPONENTS,
  SEQUENCE_STATS_DATA,
  EMITTER_ERROR,
} from "../../utils/constants";
import { DeleteConfirmationModal } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { getAllSteps } from "../../redux/actions/stepsActions";
import { getRequest, postRequest } from "../../Apis";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { getTemplateList } from "../../redux/actions/templateListActions";
import { STRINGS } from "./strings";
import { SEQUENCE_PATH } from "../../utils/constants/routes";
import CustomHeader from "../../components/Header/CustomHeader";
import TypographyInline from "../../components/Typography/TypographyInline";
import { ICON_LABELS, fontWeight } from "../../utils/constants/UI";
import {
  GREY,
  HELP,
  PRIMARY,
  TEAL,
  WARNING,
} from "../../utils/constants/colors";
import { processStatsKeyValuePairs } from "../../utils/helpers";
import StatsSkeleton from "./components/common/StatsSkeleton";
import StepsSkeleton from "./components/common/StepsSkeleton";
import EachStepLayout from "./components/common/EachStepLayout";
import { Button } from "../../components/Buttons";
import classes from "./marketing.module.css";
import SelectionAutomationOrManual from "./components/Steps/SelectionAutomationOrManual";

const StepsScreen = () => {
  const params = useParams();
  const sequenceId = params.id;
  const folderId = params.folderId;
  const URL =
    sequenceEndPoints.deleteStep + "?" + STRINGS.SEQUENCE_ID + "=" + params?.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepsSelector = useSelector((state) => state?.getAllSteps);
  const { payload } = stepsSelector;
  const [autoModal, setAutoModal] = useState(null);
  const [callModal, setCallModal] = useState(null);
  const [todoModal, setTodoModal] = useState(null);
  const [opened, setOpened] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEndpoint, setDeleteEndpoint] = useState("");
  const [stepNumber, setStepNumber] = useState(0);
  const [automatedOpen, setAutomatedOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [todoOpen, setTodoOpen] = useState(false);
  const [reSubject, setReSubject] = useState("");
  const [modStyl, setModStyl] = useState(false);
  const [delName, setDelName] = useState("");
  const [type, setType] = useState("");
  const [seqLoading, seteSeqLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [sequenceStats, setSequenceStats] = useState({});
  const [isFolderFetched, setIsFolderFetched] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [headerItems, setHeaderItems] = useState([
    { id: STRINGS.TYPE_SEQ, label: STRINGS.TYPE_SEQ, path: SEQUENCE_PATH },
  ]);

  // Stats API call
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setStatsLoading(true);
        const response = await postRequest(
          navigate,
          sequenceEndPoints.statsSteps,
          { sequence_ids: [sequenceId] },
        );
        if (mounted) {
          setSequenceStats(
            processStatsKeyValuePairs(
              response?.data?.records?.[sequenceId] || {},
            ),
          );
        }
      } catch (error) {
      } finally {
        if (mounted) {
          setStatsLoading(false);
          setOpened(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [navigate, sequenceId]);

  // Steps list API call
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        seteSeqLoading(true);
        if (folderId && !isFolderFetched) {
          const get_folder_details_payload = await getRequest(
            navigate,
            `${sequenceEndPoints.getFolderDetails}/${folderId}`,
          );
          if (mounted) {
            const new_header_items = [
              ...headerItems,
              {
                id: folderId,
                label: get_folder_details_payload?.data?.record?.name || "",
                path: `${SEQUENCE_PATH}/${folderId}`,
              },
            ];

            setHeaderItems(new_header_items);
            await dispatch(
              getAllSteps(
                sequenceEndPoints.validateSequenceSteps + sequenceId,
                navigate,
              ),
            );
            setIsFolderFetched(true);
          }
        } else {
          await dispatch(
            getAllSteps(
              sequenceEndPoints.validateSequenceSteps + sequenceId,
              navigate,
            ),
          );
        }
      } catch (error) {
      } finally {
        if (mounted) {
          seteSeqLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch, navigate]);

  // Set sequence name header
  useEffect(() => {
    if (folderId && headerItems?.length <= 2 && stepsSelector?.payload?.data) {
      setHeaderItems([
        ...headerItems,
        { id: sequenceId, label: stepsSelector?.payload?.data?.name },
      ]);
    } else if (headerItems?.length && stepsSelector?.payload?.data) {
      setHeaderItems([
        { id: STRINGS.TYPE_SEQ, label: STRINGS.TYPE_SEQ, path: SEQUENCE_PATH },
        { id: sequenceId, label: stepsSelector?.payload?.data?.name },
      ]);
    }
    setOpened(false);
  }, [stepsSelector, folderId, sequenceId]);

  const handleClone = (step, ind) => {
    if (step?.step_type === 1) {
      dispatch(getTemplateList({ name: "", type: STRINGS.FILE }, navigate));
      setAutoModal(step);
      setAutomatedOpen(true);
      setType(STRINGS.CLONE);
      setDelName(ind + 1);
      setReSubject(step?.step_data?.subject);
    }
    if (step?.step_type === 2) {
      setTodoModal(step);
      setTodoOpen(true);
      setType(STRINGS.CLONE);
      setDelName(ind + 1);
    }
    if (step?.step_type === 3) {
      setCallModal(step);
      setCallOpen(true);
      setType(STRINGS.CLONE);
      setDelName(ind + 1);
    }
  };

  const handleDelete = (step, ind) => {
    setShowDeleteModal(step);
    setDelName(`${STRINGS.STEP} ${ind + 1}`);
    setDeleteEndpoint(URL + `&${STRINGS.STEP_ID}=${step?._id}`);
  };

  const handleEdit = (step, ind) => {
    if (step?.step_type === 1) {
      dispatch(getTemplateList({ name: "", type: STRINGS.FILE }, navigate));
      setAutoModal(step);
      setAutomatedOpen(true);
      setType(STRINGS.EDIT);
      setDelName(ind + 1);
      setReSubject(step?.step_data?.subject);
    }
    if (step?.step_type === 2) {
      setTodoModal(step);
      setTodoOpen(true);
      setType(STRINGS.EDIT);
      setDelName(ind + 1);
    }
    if (step?.step_type === 3) {
      setCallModal(step);
      setCallOpen(true);
      setType(STRINGS.EDIT);
      setDelName(ind + 1);
    }
  };

  // UPDATED: Handle drag and drop with single API call
  const handleDragEnd = async (result) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    try {
      // Get the steps array
      const steps = [...payload?.data?.seq_steps];

      // Optimistically update UI by reordering the array
      const [movedStep] = steps.splice(sourceIndex, 1);
      steps.splice(destinationIndex, 0, movedStep);

      // Create the new order array with step IDs
      const stepsOrder = steps.map((step) => step._id);

      // Single API call to reorder all steps
      const reorderPayload = {
        sequence_id: sequenceId,
        steps_order: stepsOrder,
        user_id: payload?.data?.user_id || localStorage.getItem("user_id"), // Add user_id from appropriate source
      };

      await postRequest(
        navigate,
        sequenceEndPoints.reorderSteps, // NEW endpoint: add this to your constants
        reorderPayload,
      );

      EventEmitter.emit(EMITTER_SUCCESS, "Steps reordered successfully");

      // Refresh the steps list to get the updated data
      await dispatch(
        getAllSteps(
          sequenceEndPoints.validateSequenceSteps + sequenceId,
          navigate,
        ),
      );
    } catch (e) {
      EventEmitter.emit(
        EMITTER_ERROR,
        e?.data?.message || "Failed to reorder steps",
      );

      // Refresh on error to restore correct order
      await dispatch(
        getAllSteps(
          sequenceEndPoints.validateSequenceSteps + sequenceId,
          navigate,
        ),
      );
    }
  };

  const handleClose = () => {
    setAutoModal(false);
    setCallModal(false);
    setTodoModal(false);
  };

  const STEP_ACTIONS = [
    {
      name: STRINGS.COMMON_CLONE,
      icon: ICON_LABELS.CONTENT_COPY_1,
      action: handleClone,
    },
    {
      name: STRINGS.COMMON_EDIT,
      icon: ICON_LABELS.EDIT,
      action: handleEdit,
    },
    {
      name: STRINGS.DELETE,
      icon: ICON_LABELS.DELETE,
      action: handleDelete,
    },
  ];

  const handleAddStep = (index, step) => {
    setModStyl(true);
    setOpened(true);
    setStepNumber(index + 1 + 1);
    setReSubject(step?.step_data?.subject);
  };

  return (
    <Box className={classes.stepsContainer}>
      <CustomHeader items={headerItems} />
      {/* <ChooseStepModal
        open={opened}
        setOpen={setOpened}
        reSubject={reSubject}
        stepNumber={stepNumber || 1}
        custStyle={modStyl}
        showAutomated={true} // This will show the automation option
      /> */}

      <SelectionAutomationOrManual
        open={opened}
        setOpen={setOpened}
        reSubject={reSubject}
        stepNumber={stepNumber || 1}
        custStyle={modStyl}
        seqId={sequenceId}
      />
      {automatedOpen && (
        <CreateAutomatedEmail
          handleClose={handleClose}
          data={autoModal}
          open={automatedOpen}
          setOpen={setAutomatedOpen}
          editType={type}
          number={delName}
          stepLength={payload?.data?.seq_steps?.length}
          reSubject={reSubject}
        />
      )}
      {callOpen && (
        <CreateCall
          handleClose={handleClose}
          data={callModal}
          open={callOpen}
          setOpen={setCallOpen}
          editType={type}
          number={delName}
          stepLength={payload?.data?.seq_steps?.length}
        />
      )}
      {todoOpen && (
        <CreateTodo
          handleClose={handleClose}
          data={todoModal}
          open={todoOpen}
          setOpen={setTodoOpen}
          editType={type}
          number={delName}
          stepLength={payload?.data?.seq_steps?.length}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          setOpen={setShowDeleteModal}
          message={STRINGS.DELETE_STEP_CONFIRMATION}
          name={delName}
          val={STRINGS.STEP}
          action={getAllSteps}
          endPoint={deleteEndpoint}
          reFetchEndpoint={sequenceEndPoints.validateSequenceSteps + sequenceId}
        />
      )}
      <Box
        className={classes.statsParentContainer}
        style={{
          padding: "16px",
          borderRadius: "16px",
        }}
      >
        <Box
          className={classes.statsContainer}
          id="modal_body"
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.8)",
            borderRadius: "16px",
            padding: "16px 20px",
            boxShadow:
              "0 10px 30px rgba(0, 0, 0, 0.05), 0 1px 8px rgba(0, 0, 0, 0.02)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Gradient bar using the three color palettes */}
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "3px",
              opacity: 0.9,
            }}
          />

          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.STATISTICS}
            className={classes.statsTitle}
            style={{
              marginBottom: "12px",
              display: "block",
              fontSize: "14px",
              fontWeight: "700",
              letterSpacing: "0.5px",
              color: "#1f2937",
              position: "relative",
              paddingLeft: "12px",
            }}
            // Add a subtle accent line
            before={{
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              height: "70%",
              width: "3px",
              background: `linear-gradient(180deg, ${HELP.W_600}, ${TEAL.W_600})`,
              borderRadius: "2px",
            }}
          />
          <Box
            className={classes.pureFlex2}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "0",
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {statsLoading ? (
              <StatsSkeleton />
            ) : (
              SEQUENCE_STATS_DATA?.map((eachStat, index) => {
                const isLast = index === SEQUENCE_STATS_DATA.length - 1;
                // Alternate between the three color schemes for each stat
                const colorScheme =
                  index % 3 === 0 ? HELP : index % 3 === 1 ? WARNING : TEAL;

                return (
                  <Box
                    className={isLast ? classes.lastStat : classes.eachStat}
                    key={eachStat.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      padding: "12px 16px",
                      flex: "1",
                      minWidth: "0",
                      borderRight: isLast
                        ? "none"
                        : "1px solid rgba(229, 231, 235, 0.6)",
                      position: "relative",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "default",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${colorScheme.W_50} 0%, ${colorScheme.W_100} 100%)`;
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = `0 5px 15px ${colorScheme.W_200}40`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <TypographyInline
                      size={"xl"}
                      color={GREY.PRIMARY}
                      fontWeight={fontWeight.SEMI_BOLD}
                      label={sequenceStats?.[eachStat.key] || 0}
                      style={{
                        fontSize: "20px",
                        lineHeight: "1.1",
                        fontWeight: "700",
                        color: "#1f2937",
                        letterSpacing: "-0.5px",
                        transition: "all 0.3s ease",
                      }}
                    />
                    <TypographyInline
                      size={"xs"}
                      color={GREY.PRIMARY}
                      fontWeight={fontWeight.REGULAR}
                      label={eachStat.label || ""}
                      style={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        color: "#6b7280",
                        fontWeight: "500",
                        opacity: 0.9,
                      }}
                    />
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
      {/* Steps Container with Drag & Drop */}
      {seqLoading ? (
        <StepsSkeleton />
      ) : (
        <Box className={classes.stepsScrollContainer} id="modal_body">
          <Box className={classes.containAllSteps}>
            <DragDropContext
              onDragEnd={handleDragEnd}
              onDragStart={() => setIsDragging(true)}
            >
              <Droppable
                droppableId="steps-list"
                isDropDisabled={!payload?.data?.operation}
              >
                {(provided, snapshot) => (
                  <Box
                    className={classes.containOverviewSteps}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      backgroundColor: snapshot.isDraggingOver
                        ? "#f5f5f5"
                        : "transparent",
                    }}
                  >
                    {payload?.data?.seq_steps?.map((step, index) => {
                      return (
                        <React.Fragment key={step?._id}>
                          <Draggable
                            draggableId={step?._id}
                            index={index}
                            isDragDisabled={!payload?.data?.operation}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                }}
                              >
                                <EachStepLayout
                                  operation={payload?.data?.operation}
                                  sequenceType={payload?.data?.sequenceType}
                                  step={step}
                                  data={payload?.data?.seq_steps}
                                  stepIndex={index}
                                  headerActions={STEP_ACTIONS}
                                  dragHandleProps={provided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                  stepDetail={{
                                    stepNumber: step?.step_number,
                                    stepType: step?.step_type,
                                  }}
                                  body={
                                    step?.step_type === 1 ? (
                                      <STEP_TYPE_COMPONENTS.Email
                                        data={step?.step_data}
                                        reSubject={reSubject}
                                        stepNumber={stepNumber || 0}
                                      />
                                    ) : step?.step_type === 2 ? (
                                      <STEP_TYPE_COMPONENTS.Todo
                                        data={step?.step_data}
                                      />
                                    ) : step?.step_type === 3 ? (
                                      <STEP_TYPE_COMPONENTS.Call
                                        data={step?.step_data}
                                      />
                                    ) : null
                                  }
                                  footer={{
                                    left:
                                      index === 0
                                        ? ""
                                        : step?.interval > 1
                                          ? `${step?.interval} ${STRINGS.DAYS_AFTER_STEP} ${index}`
                                          : `${step?.interval} ${STRINGS.DAY_AFTER_STEP} ${index}`,
                                    right: <Box></Box>,
                                  }}
                                />
                              </Box>
                            )}
                          </Draggable>
                          <Box className={classes.horizontalBorder}></Box>
                          <Box className={classes.addStepBtnContainer}>
                            <Button
                              onClick={() => {
                                if (
                                  payload?.data?.sequenceType === "automated"
                                ) {
                                  EventEmitter.emit(
                                    EMITTER_ERROR,
                                    "Automated Sequences cannot add manual steps",
                                  );
                                } else {
                                  handleAddStep(index, step);
                                }
                              }}
                              variant="ghost"
                              size="sm36"
                              label={STRINGS.ADD_STEP}
                              disabled={
                                !payload?.data?.operation ||
                                payload?.data?.sequenceType === "automated"
                              }
                            />
                          </Box>
                          <Box className={classes.horizontalBorder}></Box>
                        </React.Fragment>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StepsScreen;
